import type React from "react";
import { createContext, useCallback, useMemo, useState } from "react";
import type { OrgGroupVisibilityRule, Role } from "@/lib/auth-types";
import type { VisibilityRule } from "@/lib/visibility-types";

export interface VisibilityContextValue {
	isVisible: (elementId: string) => boolean;
	isEditorMode: boolean;
	selectedRole: Role | null;
	selectedGroupId: number | null;
	toggleVisibility: (elementId: string) => void;
	toggleVisibilityForAll: (elementId: string) => void;
	getVisibleElements: (role: Role) => Set<string>;
	getVisibleElementsForGroup: (groupId: number) => Set<string>;
	setEditorMode: (active: boolean) => void;
	setSelectedRole: (role: Role | null) => void;
	setSelectedGroupId: (groupId: number | null) => void;
	/** Wstrzyknięcie reguł grupy edytowanej w edytorze — patrz `OrgGroupRulesSync`. */
	setEditorGroupRules: (rules: OrgGroupVisibilityRule[]) => void;
	registeredElements: Set<string>;
	registerElement: (elementId: string) => void;
}

interface VisibilityProviderProps {
	rules: VisibilityRule[];
	orgRules?: OrgGroupVisibilityRule[];
	userRoles: Role[];
	userOrgGroups?: number[]; // W jakich grupach jest aktualny user
	children: React.ReactNode;
	onToggleVisibility?: (role: Role, elementId: string, nextVisible: boolean) => void;
	onToggleVisibilityForAll?: (elementId: string, nextVisible: boolean) => void;
	onToggleOrgGroupVisibility?: (groupId: number, elementId: string, nextVisible: boolean) => void;
}

export const VisibilityContext = createContext<VisibilityContextValue | null>(null);

function isParentVisible(
	elementId: string,
	grants: Set<string>,
	registeredElements: Set<string>,
): boolean {
	const segments = elementId.split(".");
	if (segments.length <= 2) {
		return true;
	}

	const parent = segments.slice(0, -1).join(".");

	// If parent is not a registered <Visible> element, skip this level.
	// This prevents orphan organizational prefixes (e.g. "page.users")
	// from blocking child elements that ARE registered.
	if (!registeredElements.has(parent)) {
		return isParentVisible(parent, grants, registeredElements);
	}

	if (!grants.has(parent)) {
		return false;
	}

	return isParentVisible(parent, grants, registeredElements);
}

export function VisibilityProvider({
	rules,
	orgRules = [],
	userRoles,
	userOrgGroups = [],
	children,
	onToggleVisibility,
	onToggleVisibilityForAll,
	onToggleOrgGroupVisibility,
}: VisibilityProviderProps) {
	const [isEditorMode, setEditorMode] = useState(false);
	const [selectedRole, setSelectedRole] = useState<Role | null>(null);
	const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
	const [registeredElements, setRegisteredElements] = useState<Set<string>>(new Set());
	// Reguły grupy wybranej w edytorze — wstrzykiwane przez `OrgGroupRulesSync`,
	// który siedzi WEWNĄTRZ providera (tylko tam zna `selectedGroupId`) i ma dostęp
	// do QueryClienta. Provider zostaje sterowany propsami, więc testy renderujące
	// go bez React Query nadal działają.
	const [editorGroupRules, setEditorGroupRules] = useState<OrgGroupVisibilityRule[]>([]);

	const grantsByRole = useMemo(() => {
		const next = new Map<Role, Set<string>>();

		for (const rule of rules) {
			const current = next.get(rule.role) ?? new Set<string>();
			if (rule.visible) {
				current.add(rule.elementId);
			} else {
				current.delete(rule.elementId);
			}
			next.set(rule.role, current);
		}

		return next;
	}, [rules]);

	const grantsByOrgGroup = useMemo(() => {
		const next = new Map<number, Set<string>>();

		// `orgRules` to WYŁĄCZNIE grupy zalogowanego użytkownika (endpoint
		// `/visibility-rules/me/org`). `editorGroupRules` dokłada grupę wybraną
		// w edytorze — super_admin zwykle nie jest jej członkiem, więc bez tego
		// edytowałby stan pokazywany jako pusty. Dokładane na końcu: dla grupy
		// obecnej w obu źródłach wygrywa świeższy fetch edytora.
		for (const rule of [...orgRules, ...editorGroupRules]) {
			const current = next.get(rule.groupId) ?? new Set<string>();
			if (rule.visible) {
				current.add(rule.elementId);
			} else {
				current.delete(rule.elementId);
			}
			next.set(rule.groupId, current);
		}

		return next;
	}, [orgRules, editorGroupRules]);

	const userUnionGrants = useMemo(() => {
		const union = new Set<string>();
		// 1. Zawsze uwzględnij role-based grants
		for (const role of userRoles) {
			const roleGrants = grantsByRole.get(role);
			if (!roleGrants) {
				continue;
			}
			for (const elementId of roleGrants) {
				union.add(elementId);
			}
		}

		// 2. Dodatkowo uwzględnij org group grants (union, nie override)
		for (const groupId of userOrgGroups) {
			const groupGrants = grantsByOrgGroup.get(groupId);
			if (!groupGrants) continue;
			for (const elementId of groupGrants) {
				union.add(elementId);
			}
		}

		return union;
	}, [grantsByRole, grantsByOrgGroup, userRoles, userOrgGroups]);

	const getVisibleElements = useCallback(
		(role: Role): Set<string> => {
			const roleGrants = grantsByRole.get(role);
			if (!roleGrants) {
				return new Set<string>();
			}

			return new Set<string>(roleGrants);
		},
		[grantsByRole],
	);

	const getVisibleElementsForGroup = useCallback(
		(groupId: number): Set<string> => {
			const groupGrants = grantsByOrgGroup.get(groupId);
			if (!groupGrants) {
				return new Set<string>();
			}

			return new Set<string>(groupGrants);
		},
		[grantsByOrgGroup],
	);

	const isVisible = useCallback(
		(elementId: string): boolean => {
			let grants = userUnionGrants;

			if (isEditorMode) {
				if (selectedGroupId) {
					grants = getVisibleElementsForGroup(selectedGroupId);
				} else if (selectedRole) {
					grants = getVisibleElements(selectedRole);
				} else {
					grants = new Set<string>();
				}
			}

			if (!grants.has(elementId)) {
				return false;
			}

			return isParentVisible(elementId, grants, registeredElements);
		},
		[
			getVisibleElements,
			getVisibleElementsForGroup,
			isEditorMode,
			registeredElements,
			selectedRole,
			selectedGroupId,
			userUnionGrants,
		],
	);

	const toggleVisibility = useCallback(
		(elementId: string) => {
			if (!isEditorMode) {
				return;
			}

			if (selectedGroupId) {
				const currentGrants = grantsByOrgGroup.get(selectedGroupId) ?? new Set<string>();
				const nextVisible = !currentGrants.has(elementId);
				onToggleOrgGroupVisibility?.(selectedGroupId, elementId, nextVisible);
				return;
			}

			if (selectedRole) {
				const currentGrants = grantsByRole.get(selectedRole) ?? new Set<string>();
				const nextVisible = !currentGrants.has(elementId);
				onToggleVisibility?.(selectedRole, elementId, nextVisible);
			}
		},
		[
			grantsByRole,
			grantsByOrgGroup,
			isEditorMode,
			onToggleVisibility,
			onToggleOrgGroupVisibility,
			selectedRole,
			selectedGroupId,
		],
	);

	const toggleVisibilityForAll = useCallback(
		(elementId: string) => {
			if (!isEditorMode) {
				return;
			}

			// „Dla wszystkich" dotyczy WYŁĄCZNIE ról. W trybie grupy Ctrl+Alt+klik
			// otworzyłby element całej organizacji zamiast wybranej grupie — dokładne
			// przeciwieństwo tego, po co ktoś wybiera grupę (grant imienny).
			if (selectedGroupId != null) {
				console.warn(
					"[visibility] Ctrl+Alt+klik działa tylko dla ról — odznacz grupę, żeby nadać wszystkim rolom",
				);
				return;
			}

			const rolesWithGrant = [...grantsByRole.entries()].filter(([, grants]) =>
				grants.has(elementId),
			).length;
			const nextVisible = rolesWithGrant <= grantsByRole.size / 2;
			onToggleVisibilityForAll?.(elementId, nextVisible);
		},
		[grantsByRole, isEditorMode, onToggleVisibilityForAll, selectedGroupId],
	);

	const registerElement = useCallback((elementId: string) => {
		setRegisteredElements((prev) => {
			if (prev.has(elementId)) {
				return prev;
			}

			const next = new Set(prev);
			next.add(elementId);
			return next;
		});
	}, []);

	const value = useMemo<VisibilityContextValue>(
		() => ({
			isVisible,
			isEditorMode,
			selectedRole,
			selectedGroupId,
			toggleVisibility,
			toggleVisibilityForAll,
			getVisibleElements,
			getVisibleElementsForGroup,
			setEditorMode,
			setSelectedRole,
			setSelectedGroupId,
			setEditorGroupRules,
			registeredElements,
			registerElement,
		}),
		[
			getVisibleElements,
			getVisibleElementsForGroup,
			isEditorMode,
			isVisible,
			registerElement,
			registeredElements,
			selectedRole,
			selectedGroupId,
			toggleVisibility,
			toggleVisibilityForAll,
		],
	);

	return <VisibilityContext.Provider value={value}>{children}</VisibilityContext.Provider>;
}
